import { useState, useEffect, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import StatusBarSlider from './StatusBarSlider';
import AuthContext from '../../contexts/auth/AuthContext';
import { MARKETING } from '../../constants/roles/roles';

const LandingPage = ({ contentSlides }) => {
    const { currentContainer, currentRoles, isGlobalAdmin } = useContext(AuthContext);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [startX, setStartX] = useState(null);
    const [startY, setStartY] = useState(null);
    const location = useLocation();

    const isOnlyMarketingUser = currentRoles?.length === 1 && currentRoles[0] === MARKETING && !isGlobalAdmin;

    // check if the location state has a slide value and set the current index accordingly
    useEffect(() => {
        const slide = location.state?.slide;
        setCurrentIndex(typeof slide === 'number' ? slide : 0);
      }, [location.key]);

    /**
     * Navigates to the previous slide in a collection.
     * Determines the new current index by decrementing the existing index unless it is at the beginning,
     * in which case it loops back to the last slide in the collection.
     */
    const goToPrevious = () => {
        if(!currentContainer) return;
        const newIndex = currentIndex === 0 ? contentSlides.length - 1 : currentIndex - 1;
        setCurrentIndex(newIndex);
    };

    /**
     * Updates the `currentIndex` value to point to the next slide.
     * If the current slide is the last in the array, it loops back to the first slide.
     * Otherwise, it advances to the next slide in the array.
     */
    const goToNext = () => {
        if(!currentContainer) return;
        const newIndex = currentIndex === contentSlides.length - 1 ? 0 : currentIndex + 1;
        setCurrentIndex(newIndex);
    };

    /**
     * Handles the touchstart event for detecting the beginning of a swipe gesture.
     *
     * This function captures the initial touch position on the x-axis when a touch event starts.
     * The recorded position can be used to calculate swipe direction or distance in subsequent events.
     *
     * @param {TouchEvent} e - The touch event object containing details about the touch interaction.
     */
    const handleTouchStart = (e) => {
        if(!currentContainer) return;
        setStartX(e.touches[0].clientX);
        setStartY(e.touches[0].clientY);
    };

    /**
     * Handles the touchend event triggered when the user's touch interaction ends.
     * This function executes the logic to process a swipe action by invoking the `handleSwipe` method.
     *
     * @param {TouchEvent} e - The touch event object associated with the user's touch end interaction.
     */
    const handleTouchEnd = (e) => {
        if(!currentContainer) return;
        const endX = e.changedTouches[0].clientX;
        const endY = e.changedTouches[0].clientY;
        handleSwipe(endX, endY);
    };

    /**
     * Handles the mouse down event and initializes the starting number
     * with the coordinates of the mouse click.
     *
     * @param {MouseEvent} e - The mouse down event object.
     */
    const handleMouseDown = (e) => {
        if(!currentContainer) return;
        setStartX(e.clientX);
        setStartY(e.clientY);
    };

    /**
     * Handles the mouseup event triggered when the user releases a mouse button.
     * This function captures the x-coordinate of the mouse pointer at the time of the event
     * and invokes the handleTouchEnd function.
     *
     * @param {MouseEvent} e - The mouseup event object containing information about the event.
     */
    const handleMouseUp = (e) => {
        if(!currentContainer) return;
        handleSwipe(e.clientX, e.clientY);
    };

    /**
     * Handles the touchend event to determine swipe gestures based on the difference
     * between starting and ending touch coordinates. Executes specific actions for
     * "swipe left" or "swipe right" gestures by comparing this difference to a
     * defined swipe threshold.
     *
     * The function resets the touch position variables `startX` and `startY`
     * after processing the swipe logic.
     *
     * Swipe Gestures:
     * - "Swipe left": Triggers the `goToNext` function when the touch movement exceeds the threshold in the left direction.
     * - "Swipe right": Triggers the `goToPrevious` function when the touch movement exceeds the threshold in the right direction.
     */
    const handleSwipe = (endX, endY) => {
        if(!currentContainer) return;
        if (startX !== null && startY !== null && endX !== null && endY !== null) {
            const deltaX = startX - endX;
            const deltaY = startY - endY;
            const swipeThreshold = 50;

            if (Math.abs(deltaX) > Math.abs(deltaY)) {
                if (deltaX > swipeThreshold) {
                    goToNext();
                } else if (deltaX < -swipeThreshold) {
                    goToPrevious();
                }
            }
        }

        setStartX(null);
        setStartY(null);
    };

    /**
     * Navigates to a specific slide when an indicator dot is clicked.
     * @param {number} index The index of the slide to navigate to.
     */
    const goToSlide = (index) => {
        setCurrentIndex(index);
    };

    return (
        <div className="landing-page"
             onTouchStart={handleTouchStart}
             onTouchEnd={handleTouchEnd}
             onMouseDown={handleMouseDown}
             onMouseUp={handleMouseUp}>

            {
                isOnlyMarketingUser ?
                contentSlides[0] : contentSlides[currentIndex]
            }
            {/* Slide Indicators */}
            {currentContainer && !isOnlyMarketingUser &&
            <StatusBarSlider>
                <div className="slider-indicator">
                    {contentSlides.map((_, index) => (
                        <div
                            className={currentIndex === index ? 'slider-indicator-dot active' : 'slider-indicator-dot'}
                            key={index}
                            onClick={() => goToSlide(index)}
                        ></div>
                    ))}
                </div>
            </StatusBarSlider>}
        </div>
    );
};

export default LandingPage;
